import React, { Component } from 'react';
import _ from 'lodash';
import moment from 'moment';
import DocumentTitle from 'react-document-title';
import { connect } from 'react-redux';
import { Grid, Header, Icon, Message, Popup, Segment } from 'semantic-grab-ui/src';
import type { RequestStates } from 'types/redux';
import { DetailsHeader } from 'components/custom';
import IdleTimer from 'react-idle-timer';
import scribe from '@grab-x/scribe-web-sdk';
import { Tabs } from 'grab-ui';
import { hasJustSuccess, isRequestsPending } from 'utils/request-state-utils';
import { getTarget } from 'actions/trident-campaigns';
import { getCampaignMetrics, getCampaignNotSentMetrics } from 'actions/newTridentExtra';
import ProgressiveContainer from 'components/container/ProgressiveContainer';
import type { TridentCampaignDetails } from 'types/trident-campaigns';
import { nodeTypes, targetUserTypes } from 'data/trident-types';
import type { APICampaignMetricsResponse } from 'types/trident-api';
import { findNodes, getDefaultNodeName } from 'omega-rtc/utils/trident-node-utils';
import MetricsPanel from './MetricsPanel';
import {
  getScenarioBusinessMetrics,
  getScenarioCommsMetrics,
  getScenarioGroupsForMetrics,
  getScenarioNotSentMetrics,
  getScheduleBusinessMetrics,
  getScheduleCommsMetrics,
  getScheduleNotSentMetrics,
  getSchedulesGroupForMetrics,
} from './utils';
import ScribeEventName, { ScribeConfig, ScribeExtras } from 'omega-rtc/constants/scribe';
import { closeTabEvent } from 'omega-rtc/utils/scribe';
import { LAST_UPDATED_METRICS_KEY, MAM_KEY, NOT_SENT_KEY, RAW_KEY, REPORTS_KEY, DROPPED_BY_XCO_KEY } from 'omega-rtc/constants/metrics';
import { BusinessMetrics } from 'omega-rtc/views/trident-campaigns/metrics/BusinessMetrics';
import MetricsBreakdownSection from 'omega-rtc/views/trident-campaigns/metrics/MetricsBreakdownSection';
import NoMetrics from 'omega-rtc/views/ucd/NoMetrics';

interface RootNode {
  id: string;
  name: string;
  startTime: moment.Moment;
  endTime?: moment.Moment;
}

interface TridentCampaignMetricsPageProps {
  getTarget: (arg1: string, campaignID: string, options: { countryID: number }) => void;
  getCampaignMetrics: (countryID: number, campaignID: string) => void;
  getCampaignNotSentMetrics: (countryID: number, campaignID: string) => void;
  getCampaignMetricsRequest: RequestStates;
  getCampaignNotSentMetricsRequest: RequestStates;
  getCampaignRequest?: RequestStates;
  campaignMetrics: APICampaignMetricsResponse;
  campaignNotSentMetrics: APICampaignMetricsResponse;
  campaignID: string;
  countryID: number;
  webPath: string;
  campaign: TridentCampaignDetails;
  userPermissions: any;
  countries: any;
}

interface TridentCampaignMetricsPageState {
  selectedTab: string;
  rootNodes: RootNode[];
  commsMetricsMap: Record<string, any>;
  mamMetricsMap: Record<string, any>;
  rawMetricsMap: Record<string, any>;
  notSentMetricsMap: Record<string, any>;
  timeout: number;
  seletedTab?: string;
  lastUpdated?: string;
  reports?: any;
  droppedByXCO?: number;
}

// max number of days that metrics can be tracked for any scenario and schedule
const MaxMetricsWindowInDays = 30;
const MetricsUpdateLagInDays = 3;
const BusinessMetricsStartAvailabilityDate = moment('2021-06-01');
const BusinessMetricsMinimumDurationInDays = 1;

// TridentCampaignMetricsPage is the page for displaying performance metrics for one whole campaign
class TridentCampaignMetricsPage extends Component<TridentCampaignMetricsPageProps, TridentCampaignMetricsPageState> {
  idleTimer: any;

  constructor(props: TridentCampaignMetricsPageProps) {
    super(props);
    this.state = {
      selectedTab: '',
      rootNodes: [], // array of {id, name, startTime}
      commsMetricsMap: {}, // root node ID -> list of metrics,
      mamMetricsMap: {}, // root node ID -> list of metrics,
      rawMetricsMap: {},
      notSentMetricsMap: {}, // root node ID -> list of not-sent metrics
      timeout: ScribeConfig.IDLE_TIMEOUT,
    };
    scribe.sendEvent(ScribeEventName.CAMPAIGN_METRICS, { campaignId: props.campaignID, ...ScribeExtras.OPEN_PAGE });
  }

  componentDidMount() {
    this.props.getTarget('', this.props.campaignID, {
      countryID: this.props.countryID,
    });

    scribe.sendEvent(ScribeEventName.FORM_OPEN, {
      campaignId: this.props.campaignID,
    });

    // it will be trigger as soon as user close the current tab
    closeTabEvent(`${this.props.campaignID}/metrics`, () => {
      scribe.sendEvent(ScribeEventName.CAMPAIGN_METRICS, {
        campaignId: this.props.campaignID,
        ...ScribeExtras.CLOSE_PAGE,
      });
    });
  }

  componentWillUnmount() {
    // it will be trigger as soon as user navigate to another page
    scribe.sendEvent(ScribeEventName.CAMPAIGN_METRICS, {
      campaignId: this.props.campaignID,
      ...ScribeExtras.LEAVE_PAGE,
    });
  }

  componentWillReceiveProps(nextProps: TridentCampaignMetricsPageProps) {
    if (
      hasJustSuccess(
        this.props.getCampaignRequest,
        nextProps.getCampaignRequest,
      ) &&
      !_.isEmpty(nextProps.campaign)
    ) {
      // parse root nodes in the campaign
      const rootScenarioNodeIDs = _.keys(
        _.get(nextProps.campaign, 'rootNodeTmGroupMapping', {}),
      );
      const rootScheduleNodeIDs = _.keys(
        _.get(nextProps.campaign, 'rootNodeScheduleMapping', {}),
      );
      const rootNodeIDs = _.concat(rootScenarioNodeIDs, rootScheduleNodeIDs);
      this.setState({
        rootNodes: rootNodeIDs.map(id => {
          return {
            id,
            name: this.getTabName(id, nextProps.campaign),
            startTime: this.getNodeStartTime(id, nextProps.campaign),
            endTime: this.getNodeEndTime(id, nextProps.campaign),
          };
        }),
        seletedTab: _.isEmpty(rootNodeIDs) ? '' : rootNodeIDs[0],
      });

      // get campaign metrics
      this.props.getCampaignMetrics(
        this.props.countryID,
        this.props.campaignID,
      );
    }

    if (
      hasJustSuccess(
        this.props.getCampaignMetricsRequest,
        nextProps.getCampaignMetricsRequest,
      )
    ) {
      // match the metrics response with the campaign set-up
      const allCommsMetrics = _.get(nextProps.campaignMetrics, 'comms', []);
      const allMamMetrics = _.get(nextProps.campaignMetrics, MAM_KEY, []);
      const allRawMetrics = _.get(nextProps.campaignMetrics, RAW_KEY, []);
      const lastUpdatedMetrics = _.get(nextProps.campaignMetrics, LAST_UPDATED_METRICS_KEY, '');
      const droppedByXCOMetrics = _.get(nextProps.campaignMetrics, DROPPED_BY_XCO_KEY, null);
      const reports = _.get(nextProps.campaignMetrics, REPORTS_KEY, []);

      const commsMetricsMap = {};
      const mamMetricsMap = {};
      const rawMetricsMap = {};
      _.map(this.state.rootNodes, r => {
        const nodeType = _.get(this.props.campaign.nodes, `${r.id}.type`);
        if (nodeType === nodeTypes.scenarios) {
          const scenarioCommsGroups = getScenarioGroupsForMetrics(
            this.props.campaign,
            r.id,
            false,
          );
          commsMetricsMap[r.id] = getScenarioCommsMetrics(
            scenarioCommsGroups,
            allCommsMetrics,
          );
          mamMetricsMap[r.id] = getScenarioBusinessMetrics(
            scenarioCommsGroups,
            allMamMetrics,
          );
          rawMetricsMap[r.id] = getScenarioBusinessMetrics(
            getScenarioGroupsForMetrics(this.props.campaign, r.id, true),
            allRawMetrics,
          );
        } else if (nodeType === nodeTypes.schedule) {
          const scheduleCommsGroups = getSchedulesGroupForMetrics(
            this.props.campaign,
            r.id,
          );
          const scheduleID = _.get(
            this.props.campaign,
            `rootNodeScheduleMapping.${r.id}`,
          );
          commsMetricsMap[r.id] = getScheduleCommsMetrics(
            scheduleID,
            scheduleCommsGroups,
            allCommsMetrics,
          );
          mamMetricsMap[r.id] = getScheduleBusinessMetrics(
            scheduleID,
            scheduleCommsGroups,
            allMamMetrics,
          );
          rawMetricsMap[r.id] = getScheduleBusinessMetrics(
            scheduleID,
            scheduleCommsGroups,
            allRawMetrics,
          );
        }
      });
      this.setState({
        commsMetricsMap,
        mamMetricsMap,
        rawMetricsMap,
        lastUpdated: lastUpdatedMetrics,
        reports,
        droppedByXCO: droppedByXCOMetrics,
      });
    }

    // Handle not-sent metrics response separately
    if (
      hasJustSuccess(
        this.props.getCampaignNotSentMetricsRequest,
        nextProps.getCampaignNotSentMetricsRequest,
      )
    ) {
      const allNotSentMetrics = _.get(nextProps.campaignNotSentMetrics, NOT_SENT_KEY, []);
      const notSentMetricsMap = {};
      _.map(this.state.rootNodes, r => {
        const nodeType = _.get(this.props.campaign.nodes, `${r.id}.type`);
        if (nodeType === nodeTypes.scenarios) {
          const scenarioCommsGroups = getScenarioGroupsForMetrics(
            this.props.campaign,
            r.id,
            false,
          );
          notSentMetricsMap[r.id] = getScenarioNotSentMetrics(
            scenarioCommsGroups,
            allNotSentMetrics,
          );
        } else if (nodeType === nodeTypes.schedule) {
          const scheduleCommsGroups = getSchedulesGroupForMetrics(
            this.props.campaign,
            r.id,
          );
          const scheduleID = _.get(
            this.props.campaign,
            `rootNodeScheduleMapping.${r.id}`,
          );
          notSentMetricsMap[r.id] = getScheduleNotSentMetrics(
            scheduleID,
            scheduleCommsGroups,
            allNotSentMetrics,
          );
        }
      });
      this.setState({
        notSentMetricsMap,
      });
    }
  }

  // Function to fetch not-sent metrics on demand
  fetchNotSentMetrics = () => {
    this.props.getCampaignNotSentMetrics(
      this.props.countryID,
      this.props.campaignID,
    );
  }


  getDetailReports = (nodeId: string) => {
    const reports = this.state.reports; // { "tg_151858": [{ "name": "not-sent-comms", "url": "https://s3-ap-southeast-1.amazonaw.....8ac81f98f:tg_151858.csv"}],  "sc_4456": [{....}] }
    const nodeType = _.get(_.get(this.props.campaign.nodes, nodeId, {}), 'type', '');
    if (nodeType === nodeTypes.scenarios) {
      const treatmentGroupId = _.get(this.props.campaign.rootNodeTmGroupMapping, nodeId, 0);
      const scenarioReports = _.get(reports, `tg_${treatmentGroupId}`, []);
      return scenarioReports;
    } else if (nodeType === nodeTypes.schedule) {
      const scheduleId = _.get(this.props.campaign.rootNodeScheduleMapping, nodeId, 0);
      const scheduleReports = _.get(reports, `sc_${scheduleId}`, []);
      return scheduleReports;
    }
    return '';
  }

  getNodeStartTime = (nodeID: string, campaign: any): moment.Moment => {
    const node = _.get(campaign, `nodes[${nodeID}]`, {});
    if (node.type === nodeTypes.scenarios) {
      return _.get(node, 'data.meta.startTime');
    }
    if (node.type === nodeTypes.schedule) {
      return _.get(node, 'data.startTime');
    }
    return campaign.startTime; // default start time for all nodes
  };

  getNodeEndTime = (nodeID: string, campaign: any): moment.Moment => {
    const node = _.get(campaign, `nodes[${nodeID}]`, {});
    if (node.type === nodeTypes.scenarios) {
      return _.get(node, 'data.meta.endTime');
    }
    if (node.type === nodeTypes.schedule) {
      return _.get(node, 'data.endTime');
    }
    return campaign.startTime; // default end time for all nodes
  }

  getTabName = (nodeID: string, campaign: any): string => {
    const nodeType = _.get(campaign, `nodes[${nodeID}].type`);

    // for scenarios node, if there is only one scenario, return that scenario's name
    // otherwise return Scenarios x <number_of_scenarios>
    if (nodeType === nodeTypes.scenarios) {
      const subScenarios = _.get(
        campaign,
        `nodes[${nodeID}].data.scenarios`,
        [],
      );
      if (subScenarios.length === 1) {
        return _.isEmpty(subScenarios[0].name)
          ? `Scenario ${getDefaultNodeName(
            nodeID,
            nodeType,
            _.get(campaign, 'nodesOccMap', {}),
          )}`
          : subScenarios[0].name;
      }
      return `Scenarios x ${subScenarios.length}`;
    }

    // for schedule node, return its name if it has name, otherwise return Schedule <default_name>
    const name = _.get(campaign, `nodes[${nodeID}].name`);
    const defaultNodeName = getDefaultNodeName(
      nodeID,
      nodeType,
      _.get(campaign, 'nodesOccMap', {}),
    );
    return _.isEmpty(name)
      ? `${_.startCase(nodeType)} ${defaultNodeName}`
      : name;
  };

  getLastUpdatedTime = (startTime: moment.Moment): moment.Moment => {
    const now = moment();
    if (now.diff(startTime, 'days') > MaxMetricsWindowInDays) {
      return moment(startTime).add(MaxMetricsWindowInDays, 'days');
    }
    return now.add(-MetricsUpdateLagInDays, 'days');
  };

  /**
   * as soon as the user back from the idle state, new open event will be triggered (indicate as a new session)
   * @param {*} e event
   */
  onActive = () => {
    scribe.sendEvent(ScribeEventName.FORM_OPEN, {
      campaignId: this.props.campaignID,
    });
  };

  /**
   * as soon as the user does nothing on the timeout set, it will indicate that they have done their session
   * @param {*} e event
   */
  onIdle = () => {
    scribe.sendEvent(ScribeEventName.CAMPAIGN_METRICS, {
      campaignId: this.props.campaignID,
      ...ScribeExtras.IDLE_TIMEOUT,
    });
  };

  render() {
    const userType = _.get(this.props.campaign, 'targetUserType', 'passenger');
    return (
      <DocumentTitle title="View Campaign Metrics">
        <div>
          <IdleTimer
            ref={ref => {
              this.idleTimer = ref;
            }}
            element={document}
            onActive={this.onActive}
            onIdle={this.onIdle}
            debounce={250}
            timeout={this.state.timeout}
          />
          <DetailsHeader
            detailsTitle={_.get(this.props.campaign, 'name', '')}
          />
          <ProgressiveContainer
            isLoading={isRequestsPending(
              this.props.getCampaignRequest,
              this.props.getCampaignMetricsRequest,
            )}
            message="Loading campaign metrics, it might take a few seconds..."
          >
            <p style={{ height: 50 }} />
            <Segment style={{ paddingLeft: 32, paddingTop: 24 }}>
              <Header as="h2" style={{ marginTop: 8, marginBottom: 24 }}>
                Performance Metrics
              </Header>
              <Tabs
                defaultActiveKey={this.state.selectedTab}
                onChange={selectedTab => this.setState({ selectedTab })}
              >
                {_.map(this.state.rootNodes, node => {
                  const hasExpNode = !_.isEmpty(
                    findNodes(
                      node.id,
                      this.props.campaign.nodes,
                      n => _.get(n, 'type') === nodeTypes.experimentTreatment,
                    ),
                  );
                  const isChainedScenarios = _.size(_.get(this.props.campaign.nodes, `${node.id}.data.scenarios`, [])) > 1;

                  const startTime = this.getNodeStartTime(node.id, this.props.campaign);
                  const endTime = this.getNodeEndTime(node.id, this.props.campaign);
                  const metricsAvailable = _.some(
                    _.get(this.state.rawMetricsMap, node.id, []), item => item.distinctUserCount > 0
                  );

                  let notShowBizMetricsReason;
                  if (userType !== targetUserTypes.pax && userType !== targetUserTypes.moveITPax) {
                    notShowBizMetricsReason = 'campaign target user type is not passenger';
                  } else if (!hasExpNode) {
                    notShowBizMetricsReason = 'no experiment is used';
                  } else if (startTime <= BusinessMetricsStartAvailabilityDate) {
                    notShowBizMetricsReason = 'the campaign start time is before the date when raw business metrics was developed';
                  } else if (endTime.diff(startTime, 'days') < BusinessMetricsMinimumDurationInDays) {
                    notShowBizMetricsReason = 'the campaign duration is not longer than 1 day';
                  } else if (isChainedScenarios) {
                    notShowBizMetricsReason = 'the scenario is a chained scenario';
                  } else if (!metricsAvailable) {
                    notShowBizMetricsReason = 'data not available yet';
                  }

                  const showBizMetrics = _.isEmpty(notShowBizMetricsReason);
                  const nodeCommsMetrics = _.get(this.state.commsMetricsMap, node.id, []);

                  return (
                    <Tabs.TabPane
                      tab={_.truncate(node.name, { length: 20 })}
                      key={node.id}
                    >
                      {/* {(isNoCommsMetrics && !showBizMetrics) */}
                      {false
                        ? <NoMetrics bodyText={'When data is ready, it will show up here'} />
                        : (
                          <React.Fragment>
                            <Grid>
                              <Grid.Row>
                                <Grid.Column width={12}>
                                  <Header as="h3" style={{ marginTop: 8, marginBottom: 24 }}>
                                    Comms Engagement Metrics
                                  </Header>
                                  {moment().add(-MetricsUpdateLagInDays, 'days') >
                                    node.startTime && (
                                      <React.Fragment>
                                        <p style={{ color: '#707070', marginTop: -20 }}>
                                          Open this <a
                                            href="https://wiki.grab.com/display/MKT/Comms+Metrics+Alignment"
                                            target="_blank" rel="noopener noreferrer"
                                          >page</a> to learn about comms metrics definition
                                        </p>
                                        <p style={{ color: '#707070', marginTop: 8 }}>
                                          Last Updated:{' '}
                                          {moment(this.state.lastUpdated).format(
                                            'D MMM YYYY. hh:mma',
                                          )}{' '}
                                          <Popup
                                            content={`This is an estimated campaign metrics last updated time. If your campaign runs for more than ${MaxMetricsWindowInDays} days, metrics will only be tracked for the first ${MaxMetricsWindowInDays} days.`}
                                            trigger={<Icon name="info circle" />}
                                          />
                                        </p>
                                      </React.Fragment>
                                    )}
                                </Grid.Column>
                              </Grid.Row>
                            </Grid>
                            <MetricsPanel
                              metrics={nodeCommsMetrics}
                            />

                            <MetricsBreakdownSection
                              nodeType={_.get(_.get(this.props.campaign.nodes, node.id, {}), 'type', '')}
                              metrics={nodeCommsMetrics}
                              reports={this.getDetailReports(node.id)}
                              campaignID={this.props.campaignID}
                              notSentMetrics={_.get(this.state.notSentMetricsMap, node.id, [])}
                              onFetchNotSentMetrics={this.fetchNotSentMetrics}
                              isLoadingNotSentMetrics={isRequestsPending(this.props.getCampaignNotSentMetricsRequest)}
                              droppedByXCO={this.state.droppedByXCO}
                            />
                            {showBizMetrics ? (
                              <BusinessMetrics
                                hasExpNode={hasExpNode}
                                countryID={this.props.countryID}
                                rawMetricsMap={this.state.rawMetricsMap}
                                node={node}
                                mamMetricsMap={this.state.mamMetricsMap}
                              />
                            )
                              :
                              <Message>
                                <Message.Header>Note</Message.Header>
                                <p>
                                  {`Raw business metrics are not shown here because ${notShowBizMetricsReason}`} <br />
                                  Learn more about raw business metrics <a
                                    href="https://wiki.grab.com/display/REW/Trident+Campaign+Performance+Metrics"
                                    target="_blank" rel="noopener noreferrer"
                                  >here</a>
                                </p>
                              </Message>
                            }
                          </React.Fragment>
                        )
                      }
                    </Tabs.TabPane>
                  );
                })}
              </Tabs>
            </Segment>
          </ProgressiveContainer>
        </div>
      </DocumentTitle>
    );
  }

  isCommMetricsEmpty = (nodeCommsMetrics: Map<string, any>): boolean => {
    for (const commsMetrics of nodeCommsMetrics.values()) {
      if (!_.isEmpty(commsMetrics.metrics)) {
        return false;
      }
    }
    return true;
  }
}

function mapStateToProps(state: any, ownProps: any) {
  const { stateKey, params = {}, webPath } = ownProps;
  const entityState = _.get(state, stateKey, {});
  return {
    webPath,
    user: state.app.user,
    userPermissions: state.app.user.userPermissions,
    campaignID: params.entityID,
    countryID: params.countryID,
    campaign: entityState.active,
    getCampaignRequest: _.get(
      state.requests,
      `getEntity${_.upperFirst(stateKey)}Request`,
      {},
    ),
    getCampaignMetricsRequest: _.get(
      state.requests,
      'getCampaignMetricsRequest',
      {},
    ),
    campaignMetrics: _.get(state, 'newTridents.campaignMetrics', {}),
    getCampaignNotSentMetricsRequest: _.get(
      state.requests,
      'getCampaignNotSentMetricsRequest',
      {},
    ),
    campaignNotSentMetrics: _.get(state, 'newTridents.campaignNotSentMetrics', {}),
  };
}

export default connect(mapStateToProps, {
  getTarget,
  getCampaignMetrics,
  getCampaignNotSentMetrics,
})(TridentCampaignMetricsPage);
